import {createAsyncThunk} from '@reduxjs/toolkit'
import AxiosClient from '../AxiosClient'
import {
  OrderPackingSlipDownloadType,
  parseOrderDocumentDownloadData,
} from 'src/app/pages/orders/orderPackingSlipUtils'

// export const getOrdersListAsync = createAsyncThunk(
//   'orders/getOrdersListAsync',
//   async function (params:{id:string, subparams:any  }, toolkit) {
//     return await AxiosClient('GET', `/api/platform-orders-list`, [], toolkit, params.subparams,  )
//   }
// )

export const getOrdersListAsync = createAsyncThunk(
  'orders/getOrdersListAsync',
  async function (
    params: {
      paramData: {page: number; limit?: number; sort_by: string; sort_order: string; status_filters?: {};api_created_at_min?:string;
        api_created_at_max:string}
      selectedPlatform: string
    },
    toolkit
  ) {
    console.log('Order Params', params)
    return await AxiosClient(
      'GET',
      `/api/platform-orders-list/${params.selectedPlatform}`,
      [],
      toolkit,
      params.paramData
    )
  }
)

export const getOrderDetailsAsync = createAsyncThunk(
  'orders/getOrderDetailsAsync',
  async function (input: string | { id: string, query?: any }, toolkit) {
    let orderId: string;
    let queryParams: any = {};

    if (typeof input === 'string') {
      orderId = input;
    } else {
      orderId = input.id;
      queryParams = input.query || {};
    }
    return await AxiosClient('GET', `/api/platform-orders/${orderId}`, [], toolkit, queryParams);
  }
);

export const orderResyncAsync = createAsyncThunk(
  'orders/orderResyncAsync',
  async function (payload: {failed_order_ids: number[]}, toolkit) {
    return await AxiosClient('POST', `/api/resync-failed-orders`, payload, toolkit, [])
  }
)

export const onholdOrderResyncAsync = createAsyncThunk(
  'orders/onholdOrderResyncAsync',
 async function (payload: {}, toolkit) {
    return await AxiosClient('POST', `/api/ready-to-sync-hold-orders`,payload,toolkit,[]);
  }
);

export const shippingResyncAsync = createAsyncThunk(
  'orders/shippingResyncAsync',
  async function (payload: {failed_shipping_ids: number[]}, toolkit) {
    return await AxiosClient('POST', `/api/resync-failed-order-shipment`, payload, toolkit, [])
  }
)

export const resyncFailedShippingLabelAsync = createAsyncThunk(
  'orders/resyncFailedShippingLabelAsync',
  async function (payload: {failed_shipping_label_order_ids: number[]}, toolkit) {
    return await AxiosClient('POST', `/api/resync-failed-shipping-label`, payload, toolkit, [])
  }
)

export const getOrderPackageByOrderIdAsync = createAsyncThunk(
  'orders/getOrderPackageByOrderIdAsync',
  async function (orderId: string | number, toolkit) {
    return await AxiosClient('GET', `/api/order-packages/${orderId}`, [], toolkit, [])
  }
)

export const saveOrderPackageAsync = createAsyncThunk(
  'orders/saveOrderPackageAsync',
  async function (
    payload: {
      order_id: number
      package_length: number
      package_width: number
      package_height: number
      package_weight: number
      weight_unit: string
      dimension_unit: string
    },
    toolkit
  ) {
    return await AxiosClient('POST', `/api/order-packages`, payload, toolkit, [])
  }
)

export const requestNewShippingLabelAsync = createAsyncThunk(
  'orders/requestNewShippingLabelAsync',
  async function (payload: {order_ids: number[]}, toolkit) {
    return await AxiosClient('POST', `/api/generate-shipping-label-request`, payload, toolkit, [])
  }
)

export const downloadShippingLabelsAsync = createAsyncThunk(
  'orders/downloadShippingLabelsAsync',
  async function (
    params: {
      platform_id: number | string
      order_ids: number[]
    },
    toolkit
  ) {
    // Format order_ids as JSON string for the API
    const queryParams = {
      platform_id: params.platform_id,
      order_ids: JSON.stringify(params.order_ids)
    }
    
    const response = await AxiosClient(
      'GET',
      `/api/download-shipping-label`,
      [],
      toolkit,
      queryParams
    )
    
    // AxiosClient returns toolkit.fulfillWithValue(), so we need to access the payload
    // Response structure: { status_code: 200, massage: "ok", data: [{ order_id: number, label_url: string[] }, ...] }
    const responseData = response?.payload || response
    
    if (responseData?.data && Array.isArray(responseData.data)) {
      const allUrls: string[] = []
      responseData.data.forEach((item: any) => {
        if (item.label_url && Array.isArray(item.label_url)) {
          allUrls.push(...item.label_url)
        }
      })
      return allUrls
    }
    
    return []
  }
)

const getInvoiceUrlsFromResponse = (data: any): string[] => {
  if (!data) return []
  if (typeof data === 'string') return [data]
  if (Array.isArray(data)) {
    return data
      .map((item: any) => {
        if (typeof item === 'string') return item
        return item?.url || item?.invoice_url || item?.invoiceUrl || item?.file_url || item?.fileUrl
      })
      .filter(Boolean)
  }
  if (typeof data === 'object') {
    const urls = data?.invoice_urls || data?.invoiceUrls || data?.urls
    if (Array.isArray(urls)) {
      return urls.filter((url: any) => typeof url === 'string' && url)
    }
    return [data?.url || data?.invoice_url || data?.invoiceUrl || data?.file_url || data?.fileUrl].filter(Boolean)
  }
  return []
}

export const downloadOrderPackingSlipAsync = createAsyncThunk(
  'orders/downloadOrderPackingSlipAsync',
  async function (
    params: {
      order_ids: number[]
      type: OrderPackingSlipDownloadType
    },
    toolkit
  ) {
    if (!params.order_ids.length) {
      throw new Error('Please select at least one order.')
    }

    const queryParams = {
      order_ids: JSON.stringify(params.order_ids),
      type: params.type,
    }

    const response = await AxiosClient(
      'GET',
      `/api/order-packing-slip-download`,
      [],
      toolkit,
      queryParams,
      toolkit.signal
    )

    const responseData = response?.payload || response
    const statusCode = Number(responseData?.status_code ?? responseData?.status)
    const data =
      responseData?.data && typeof responseData.data === 'object' ? responseData.data : null

    if (Number.isFinite(statusCode) && (statusCode < 200 || statusCode >= 300)) {
      throw new Error(
        data?.message ||
          data?.massage ||
          responseData?.message ||
          responseData?.massage ||
          'Failed to download order documents.'
      )
    }

    const {items, urls} = parseOrderDocumentDownloadData(data)

    if (!urls.length) {
      throw new Error(
        data?.message ||
          data?.massage ||
          responseData?.message ||
          responseData?.massage ||
          'No documents available for the selected orders.'
      )
    }

    return {
      items,
      urls,
      message:
        responseData?.massage ||
        responseData?.message ||
        data?.message ||
        data?.massage ||
        'Documents downloaded successfully.',
      shippingLabelRequestMessage: data?.shipping_label_request?.message ?? null,
    }
  }
)

export const downloadOrderInvoiceAsync = createAsyncThunk(
  'orders/downloadOrderInvoiceAsync',
  async function (params: {order_id?: number; order_ids?: number[]}, toolkit) {
    try {
      const orderIds = params.order_ids?.length ? params.order_ids : params.order_id ? [params.order_id] : []
      const queryParams = {order_ids: JSON.stringify(orderIds)}

      const response = await AxiosClient(
        'GET',
        `/api/order-invoice-download`,
        [],
        toolkit,
        queryParams,
        toolkit.signal
      )

      const responseData = response?.payload || response
      const errorData = responseData?.data && typeof responseData.data === 'object' ? responseData.data : null
      const invoiceUrls = getInvoiceUrlsFromResponse(responseData?.data)
      const statusCode = Number(responseData?.status_code ?? responseData?.status ?? errorData?.status_code)

      if ((Number.isFinite(statusCode) && (statusCode < 200 || statusCode >= 300)) || !invoiceUrls.length) {
        throw new Error(
          errorData?.message ||
            errorData?.massage ||
            responseData?.message ||
            responseData?.massage ||
            'Invoice URL not found.'
        )
      }

      invoiceUrls.forEach((invoiceUrl, index) => {
        const link = document.createElement('a')
        link.href = invoiceUrl
        link.download = `order-invoice-${orderIds[index] || index + 1}.pdf`
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        link.remove()
      })

      return toolkit.fulfillWithValue({
        success: true,
        invoiceUrls,
        message: responseData?.message || responseData?.massage || 'Invoice downloaded successfully.',
      })
    } catch (error: any) {
      return toolkit.rejectWithValue(error?.response?.data || {success: false, message: error?.message})
    }
  }
)

